import { useEffect } from 'react'
import { generatePath, Link, useParams } from 'react-router-dom'
import { Anchor, Container, Group, Skeleton, Stack } from '@mantine/core'

import {
  useAuthenticatedGraphMutation,
  useAuthenticatedGraphQuery,
} from '@/hooks/use-authenticated-query'
import { GetOfferGql } from '@/graphql/queries/offers/get-offer.graphql'
import { AlertLevel, IconAlert } from '@shared/components/icon-alert/icon-alert'
import { OfferStatus } from '@loom-api-types'
import { OfferForm } from '@shared/components/offers/offer-form/offer-form'
import { OfferFormDecide } from '@shared/components/offers/offer-form-decide/offer-form-decide'
import { routes } from '@/routing/routes'
import { useNavigation } from '@/hooks/use-navigation'
import { SubmitOfferGql } from '@/graphql/mutations/offers/submit-offer.graphql'
import { showSuccessNotification } from '@shared/helpers/notifications/notifications'
import { OffersTabValue } from '@/pages/offers/offers.page'

export function ViewOfferPage() {
  const { offerId } = useParams()
  const { navigateToViewOffers } = useNavigation()

  // If no offer is present in the URL, navigate to the offer list
  useEffect(() => {
    if (!offerId) {
      navigateToViewOffers()
    }
  }, [offerId])

  // Queries and mutations

  const {
    data: offerData,
    isFetching,
    isError,
    error,
  } = useAuthenticatedGraphQuery({
    queryKey: ['getOffer', offerId],
    gql: GetOfferGql,
    queryParams: { offerId },
    enabled: !!offerId,
  })

  const submitOfferMutation = useAuthenticatedGraphMutation({
    mutationKey: ['submitOffer', offerId],
    gql: SubmitOfferGql,
    gqlKey: 'submitOffer',
    onSuccess: () => {
      showSuccessNotification('Offer decisions submitted successfully')
      navigateToViewOffers()
    },
  })

  const offer = offerData?.offer?.offer
  const stationBrand = offer?.stationBrand

  const errorTitle =
    isError && error.response?.status === 403 ? 'Restricted Access' : 'An error has occurred'
  const errorMessage =
    isError && error.response?.status === 403
      ? 'You do not have permission to view this offer.'
      : 'Please try again later.'

  return (
    <Container px={0}>
      <Stack>
        <Group>
          <Anchor
            component={Link}
            size='sm'
            to={generatePath(routes.offersTab.path, { offersTabValue: OffersTabValue.VIEW_OFFERS })}
          >
            ⟵ Back to Offers
          </Anchor>
        </Group>

        {isError && (
          <IconAlert level={AlertLevel.ERROR} title={errorTitle} message={errorMessage} />
        )}

        {isFetching && <Skeleton height={400} />}

        {!isFetching && !isError && offer && (
          <>
            {/* Show offer form is under form */}
            {offer.status === OfferStatus.UnderOffer && (
              <OfferFormDecide
                offer={offer}
                brand={stationBrand || ''}
                submitOfferMutation={submitOfferMutation}
              />
            )}

            {/* Show alerts if offer is cancelled, expired, or complete */}
            {offer.status === OfferStatus.Cancelled && (
              <IconAlert level={AlertLevel.ERROR} title='This offer has been cancelled' />
            )}
            {offer.status === OfferStatus.Expired && (
              <IconAlert level={AlertLevel.ERROR} title='This offer has expired' />
            )}
            {offer.status === OfferStatus.Complete && (
              <IconAlert
                level={AlertLevel.INFO}
                title='This offer has already been responded to and is now completed'
              />
            )}

            {/* Show offer and alert if offer is expired or completed */}
            {(offer.status === OfferStatus.Complete || offer.status === OfferStatus.Expired) && (
              <OfferForm
                readOnly
                brand={stationBrand || ''}
                offer={offer}
                sentDateTime={offer.created}
              />
            )}
          </>
        )}
        <Group justify='flex-end'>
          <Anchor
            component={Link}
            size='sm'
            to={generatePath(routes.offersTab.path, { offersTabValue: OffersTabValue.VIEW_OFFERS })}
          >
            ⟵ Back to Offers
          </Anchor>
        </Group>
      </Stack>
    </Container>
  )
}
